import { z } from 'zod';
import { checkIntegrity, forkDocument, isSealed, } from '@orbit-mind/domain';
import { attachmentSchema, mapDocumentSchema } from '@orbit-mind/validators';
import { fromBase64Url, toBase64Url, } from '@orbit-mind/shared-types';
/** A bounded, portable snapshot. History, credentials and uncommitted drafts are not exported. */
export const MAX_BACKUP_BYTES = 100 * 1024 * 1024;
export const BACKUP_EXTENSION = '.orbitbackup';
const hashSchema = z.string().regex(/^[a-f0-9]{64}$/);
const envelopeSchema = z
    .object({
    format: z.literal('orbit-mind-backup'),
    version: z.literal(1),
    createdAt: z.number().int().nonnegative(),
    document: z.string().max(MAX_BACKUP_BYTES),
    checksum: hashSchema,
    files: z
        .array(z.object({ attachment: attachmentSchema, data: z.string().max(36 * 1024 * 1024) }).strict())
        .max(10000),
})
    .strict();
const encoder = new TextEncoder();
function references(document) {
    const ids = new Map();
    for (const node of Object.values(document.nodes)) {
        for (const id of node.attachments ?? [])
            ids.set(id, node.id);
        const content = node.metadata?.content;
        if (content !== null &&
            typeof content === 'object' &&
            !Array.isArray(content) &&
            'attachmentId' in content &&
            typeof content.attachmentId === 'string')
            ids.set(content.attachmentId, node.id);
    }
    return ids;
}
function validateDocument(document) {
    mapDocumentSchema.parse(document);
    if (checkIntegrity(document).length > 0)
        throw new Error('O mapa contém referências inválidas. Abra a recuperação antes de fazer backup.');
}
export async function createMapBackup(input) {
    validateDocument(input.document);
    const document = JSON.stringify(input.document);
    let total = encoder.encode(document).byteLength;
    const files = [];
    for (const id of references(input.document).keys()) {
        const file = await input.readFile(id);
        if (file === undefined)
            throw new Error('Um anexo está indisponível. Sincronize os arquivos antes de fazer backup.');
        attachmentSchema.parse(file.attachment);
        if (file.attachment.id !== id ||
            file.attachment.mapId !== input.document.map.id ||
            file.bytes.length !== file.attachment.size ||
            (await input.hash(file.bytes)) !== file.attachment.checksum)
            throw new Error('Um anexo falhou na verificação do backup.');
        total += Math.ceil((file.bytes.length * 4) / 3) + JSON.stringify(file.attachment).length;
        if (file.bytes.length > 25 * 1024 * 1024 || total > MAX_BACKUP_BYTES)
            throw new Error('Este backup excede o limite de 100 MB.');
        files.push({
            attachment: { ...file.attachment, workspaceId: input.document.map.workspaceId },
            data: toBase64Url(file.bytes),
        });
    }
    const text = JSON.stringify({
        format: 'orbit-mind-backup',
        version: 1,
        createdAt: input.now,
        document,
        checksum: await input.hash(encoder.encode(document)),
        files,
    });
    if (encoder.encode(text).byteLength > MAX_BACKUP_BYTES)
        throw new Error('Este backup excede o limite de 100 MB.');
    return text;
}
/** Validates everything before any local write; the result is also the import preview. */
export async function readMapBackup(text, hash) {
    if (text.length > MAX_BACKUP_BYTES || encoder.encode(text).byteLength > MAX_BACKUP_BYTES)
        throw new Error('Limite do backup: 100 MB.');
    const parsed = envelopeSchema.safeParse(JSON.parse(text));
    if (!parsed.success)
        throw new Error('Backup inválido ou versão ainda não suportada.');
    const value = parsed.data;
    if ((await hash(encoder.encode(value.document))) !== value.checksum)
        throw new Error('O mapa do backup foi alterado ou está corrompido.');
    const document = mapDocumentSchema.parse(JSON.parse(value.document));
    validateDocument(document);
    const expected = references(document);
    const seen = new Set();
    const files = [];
    for (const file of value.files) {
        const attachment = file.attachment;
        if (seen.has(attachment.id) ||
            !expected.has(attachment.id) ||
            attachment.mapId !== document.map.id ||
            document.nodes[attachment.nodeId] === undefined ||
            attachment.workspaceId !== document.map.workspaceId)
            throw new Error('As referências dos anexos do backup são inválidas.');
        seen.add(attachment.id);
        const bytes = fromBase64Url(file.data);
        if (bytes === null ||
            bytes.length > 25 * 1024 * 1024 ||
            bytes.length !== attachment.size ||
            (await hash(bytes)) !== attachment.checksum)
            throw new Error('Um anexo do backup está corrompido.');
        files.push({ attachment, bytes });
    }
    if (seen.size !== expected.size)
        throw new Error('O backup está incompleto: faltam anexos.');
    if (isSealed(document) && files.length > 0)
        throw new Error('Este formato não suporta anexos em mapas protegidos.');
    return { document, files, createdAt: value.createdAt };
}
/** Fresh map, topic, edge and attachment identities; the originals are never overwritten. */
export function prepareBackupCopy(backup, target) {
    const copy = forkDocument(backup.document, target.mapId, backup.document.map.title, target.now);
    const document = {
        ...copy.document,
        map: { ...copy.document.map, ownerId: target.ownerId, workspaceId: target.workspaceId },
    };
    const files = backup.files.map(({ attachment, bytes }) => {
        const id = copy.attachmentIds.get(attachment.id);
        const nodeId = copy.nodeIds.get(attachment.nodeId);
        if (id === undefined || nodeId === undefined)
            throw new Error('Referência inválida ao restaurar o anexo.');
        // Preview paths belong to the old device; renderers regenerate them from the verified bytes.
        const { previewKey: _preview, thumbnailKey: _thumbnail, ...rest } = attachment;
        return {
            bytes,
            attachment: {
                ...rest,
                id,
                nodeId,
                mapId: target.mapId,
                workspaceId: target.workspaceId,
                createdBy: target.ownerId,
                storageKey: `local/${target.mapId}/${attachment.checksum}`,
            },
        };
    });
    return { document, files };
}
//# sourceMappingURL=backup.js.map